import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Phone, User, BookOpen, CalendarClock, MapPin } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'sonner';

export default function FunnelPage() {
  const navigate = useNavigate();
  const [funnel, setFunnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lostReasons, setLostReasons] = useState([]);
  const [lostModal, setLostModal] = useState(false);
  const [lostForm, setLostForm] = useState({ lost_reason_id: '', lost_notes: '' });
  const [pendingDrop, setPendingDrop] = useState(null);

  const loadFunnel = () => {
    setLoading(true);
    API.get('/funnel').then(r => { setFunnel(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => {
    loadFunnel();
    API.get('/masters/lost-reasons').then(r => setLostReasons(r.data)).catch(() => {});
  }, []);

  const applyOptimisticMove = (draggableId, newStageId, destIndex) => {
    setFunnel(prev => {
      const updated = prev.map(col => ({
        ...col,
        leads: col.leads.filter(l => l.id !== draggableId),
        count: col.leads.filter(l => l.id !== draggableId).length
      }));
      const srcCol = prev.find(c => c.leads.some(l => l.id === draggableId));
      const lead = srcCol?.leads.find(l => l.id === draggableId);
      if (lead) {
        const destIdx = updated.findIndex(c => c.stage.id === newStageId);
        if (destIdx !== -1) {
          updated[destIdx].leads.splice(destIndex, 0, lead);
          updated[destIdx].count = updated[destIdx].leads.length;
        }
      }
      return updated;
    });
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, destination } = result;
    const newStageId = destination.droppableId;

    // Check if target is the "Lost" column
    const destCol = funnel.find(c => c.stage.id === newStageId);
    if (destCol && destCol.stage.name.toLowerCase() === 'lost') {
      // Show lost reason modal instead of moving immediately
      setPendingDrop({ draggableId, newStageId, destIndex: destination.index });
      setLostForm({ lost_reason_id: '', lost_notes: '' });
      setLostModal(true);
      return;
    }

    // Normal move
    applyOptimisticMove(draggableId, newStageId, destination.index);
    try {
      await API.put(`/leads/${draggableId}/stage`, { stage_id: newStageId });
      toast.success('Stage updated');
    } catch {
      toast.error('Failed to update stage');
      loadFunnel();
    }
  };

  const confirmLostDrop = async () => {
    if (!lostForm.lost_reason_id) {
      toast.error('Please select a reason');
      return;
    }
    if (!pendingDrop) return;
    const { draggableId, newStageId, destIndex } = pendingDrop;
    applyOptimisticMove(draggableId, newStageId, destIndex);
    setLostModal(false);
    try {
      await API.put(`/leads/${draggableId}/stage`, {
        stage_id: newStageId,
        lost_reason_id: lostForm.lost_reason_id,
        lost_notes: lostForm.lost_notes
      });
      toast.success('Lead marked as Lost');
    } catch {
      toast.error('Failed to update stage');
      loadFunnel();
    }
    setPendingDrop(null);
    setLostForm({ lost_reason_id: '', lost_notes: '' });
  };

  if (loading) return <div className="p-6"><div className="animate-pulse flex gap-4">{[1,2,3,4].map(i => <div key={i} className="w-72 h-96 bg-slate-100 rounded-xl" />)}</div></div>;

  return (
    <div className="p-6 space-y-4" data-testid="funnel-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Admissions Funnel</h1>
        <p className="text-sm text-slate-500 mt-1">Drag leads between stages to update their progress</p>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: 'calc(100vh - 200px)' }}>
          {funnel.map(col => (
            <Droppable key={col.stage.id} droppableId={col.stage.id}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`kanban-column flex flex-col ${snapshot.isDraggingOver ? 'bg-blue-50 border-blue-200' : ''}`}
                >
                  {/* Column Header */}
                  <div className="p-3 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider truncate">{col.stage.name}</h3>
                      <Badge variant="secondary" className="text-xs font-semibold">{col.count}</Badge>
                    </div>
                  </div>

                  {/* Cards */}
                  <ScrollArea className="flex-1 p-2">
                    <div className="space-y-2">
                      {col.leads.map((lead, idx) => (
                        <Draggable key={lead.id} draggableId={lead.id} index={idx}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`kanban-card ${snapshot.isDragging ? 'shadow-lg ring-2 ring-slate-900/10' : ''}`}
                              onClick={() => navigate(`/leads/${lead.id}`)}
                              data-testid={`funnel-card-${lead.id}`}
                            >
                              <p className="text-sm font-medium text-slate-900 truncate">{lead.full_name}</p>
                              <div className="mt-2 space-y-1">
                                {lead.phone_number && (
                                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                    <Phone size={11} /> {lead.phone_number}
                                  </div>
                                )}
                                {lead.course_name && (
                                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                    <BookOpen size={11} /> {lead.course_name}
                                  </div>
                                )}
                                {lead.counsellor_name && (
                                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                    <User size={11} /> {lead.counsellor_name}
                                  </div>
                                )}
                                {lead.next_follow_up && (
                                  <div className="flex items-center gap-1.5 text-xs text-amber-600">
                                    <CalendarClock size={11} /> {lead.next_follow_up}
                                  </div>
                                )}
                                {lead.source_name && (
                                  <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                    <MapPin size={11} /> {lead.source_name}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {/* Lost Reason Modal */}
      <Dialog open={lostModal} onOpenChange={v => { if (!v) { setLostModal(false); setPendingDrop(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reason for Marking as Lost</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Please select a reason for marking this lead as lost.</p>
          <div className="space-y-3 mt-2">
            <Select value={lostForm.lost_reason_id} onValueChange={v => setLostForm(p => ({ ...p, lost_reason_id: v }))} data-testid="funnel-lost-reason-select">
              <SelectTrigger data-testid="funnel-lost-reason-trigger"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>
                {lostReasons.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea placeholder="Additional notes (optional)" value={lostForm.lost_notes}
              onChange={e => setLostForm(p => ({ ...p, lost_notes: e.target.value }))}
              className="min-h-[80px]" data-testid="funnel-lost-reason-notes" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setLostModal(false); setPendingDrop(null); }} data-testid="funnel-lost-cancel">Cancel</Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmLostDrop} data-testid="funnel-lost-confirm">Confirm Lost</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
